<template>
  <div
    class="lego-checkbox lego-input"
    :class="checkboxClass"
    :tabindex="isDisabled"
    @keydown.enter.stop.prevent="onKeydownEnter"
    @keydown.space.stop.prevent="onKeydownSpace"
  >
    <div
      class="lego-checkbox__icon"
      :class="{
        'lego-checkbox__hide': hide,
        'lego-checkbox__unchecked': !clicked,
        'lego-checkbox__checked': clicked || indeterminate
      }"
      @click.stop="onClick"
    >
      <lego-icon v-if="clicked && !indeterminate" icon="check" icon-type="df" :regular="!clicked" />
      <lego-icon v-if="indeterminate" icon="bar" icon-type="df" />
    </div>
    <span
      v-if="!!$slots.default"
      class="lego-checkbox__label"
      v-lego-tooltip="$slots.default()[0].text || ''"
      @click.stop="onClick"
      ><!--
            $ver: v0.1.0
            체크박스 아이콘 오른쪽으로 위치하는 label 영역입니다. text를 넣어주는 경우가 일반적입니다.
            # default label area on the right side of checkbox icon.
        --><slot /><!--
    --></span>
  </div>
</template>
<script>
import _ from 'lodash';

export default {
  emits: ['click'],
  props: {
    readonly: {
      type: Boolean,
      default: false
    },
    hide: {
      type: Boolean,
      default: false
    },
    indeterminate: {
      type: Boolean,
      default: false
    },
    valueKey: { type: [String, Array], default: '' }
  },
  computed: {
    checkboxClass() {
      let clazz = [];
      if (
        this.disabled ||
        (this.legoCheckboxWrapper &&
          this.legoCheckboxWrapper[this.group] &&
          this.legoCheckboxWrapper[this.group].disabled)
      ) {
        clazz.push('lego-checkbox--disabled');
      }
      if (
        this.readonly ||
        (this.legoCheckboxWrapper &&
          this.legoCheckboxWrapper[this.group] &&
          this.legoCheckboxWrapper[this.group].readonly)
      ) {
        clazz.push('lego-checkbox--readonly');
      }
      clazz.push('lego-checkbox--' + this.$_size);
      if (
        this.legoCheckboxWrapper &&
        this.legoCheckboxWrapper[this.group] &&
        this.legoCheckboxWrapper[this.group].hasError
      ) {
        clazz.push('lego-validator--error');
      }
      return clazz;
    },
    clicked() {
      if (typeof this.value == 'boolean') {
        // Boolean
        if (this.actualVModel === 'Y') return true;
        if (this.actualVModel === 'N') return false;
        return this.actualVModel;
      } else {
        if (Array.isArray(this.actualVModel)) {
          // Array
          if (typeof this.value === 'object') {
            if (this.valueKey) {
              return (
                _.findIndex(this.actualVModel, (item) => {
                  if (typeof this.valueKey === 'object') {
                    return this.isCheckValueKeyTypeOfArray(item, this.value);
                  }
                  return item[this.valueKey] === this.value[this.valueKey];
                }) >= 0
              );
            } else {
              return _.findIndex(this.actualVModel, this.value) >= 0;
            }
          } else {
            return this.actualVModel.indexOf(this.value) >= 0;
          }
        } else {
          // String
          return this.actualVModel === this.value;
        }
      }
    }
  },
  methods: {
    toggleValue(e) {
      // $ver: v0.1.2
      // $arg: click event
      // 체크박스 클릭 시 발생하는 이벤트 입니다.
      // # This event occurs when the checkbox is clicked.
      this.$emit('click', this.value);
      if (this.legoCheckboxWrapper && this.legoCheckboxWrapper[this.group]) {
        this.legoCheckboxWrapper[this.group].click(e);
      }

      if (typeof this.value === 'boolean') {
        if (this.actualVModel === 'Y') {
          this.emit('N');
        } else if (this.actualVModel === 'N') {
          this.emit('Y');
        } else {
          this.emit(!this.actualVModel);
        }
      } else {
        if (Array.isArray(this.actualVModel)) {
          const findCheck = this.valueKey
            ? _.findIndex(this.actualVModel, (item) => {
                if (typeof this.valueKey === 'object') {
                  return this.isCheckValueKeyTypeOfArray(item, this.value);
                }
                return item[this.valueKey] === this.value[this.valueKey];
              }) >= 0
            : _.findIndex(this.actualVModel, this.value) >= 0;
          if (typeof this.value === 'object' && findCheck) {
            // if exist, remove it
            if (this.valueKey) {
              this.emit(
                this.actualVModel.filter((item) => {
                  if (typeof this.valueKey === 'object')
                    return !this.isCheckValueKeyTypeOfArray(item, this.value);
                  return !_.isEqual(item[this.valueKey], this.value[this.valueKey]);
                })
              );
            } else {
              this.emit(this.actualVModel.filter((item) => !_.isEqual(item, this.value)));
            }
          } else if (this.actualVModel.indexOf(this.value) >= 0) {
            // if exist, remove it
            this.emit(this.actualVModel.filter((item) => item !== this.value));
          } else {
            // if not exist, append it
            this.emit(this.actualVModel.concat(this.value));
          }
        } else {
          // String type v-model
          this.emit(this.actualVModel === this.value ? '' : this.value);
        }
      }
    },
    isCheckValueKeyTypeOfArray(item, itemCheck) {
      let check = true;
      for (let i = 0; i < this.valueKey.length; i++) {
        if (item[this.valueKey[i]] !== itemCheck[this.valueKey[i]]) {
          check = false;
          break;
        }
      }
      return check;
    }
  }
};
</script>
<style lang="scss" scoped>
.lego-checkbox {
  align-items: center;

  &:focus {
    &:after {
      display: none;
    }
  }

  &__icon {
    position: relative;
    width: 16px;
    height: 16px;
    border-radius: 2px;
  }

  &__unchecked {
    border: 1px solid #b3b7c1;
    background: $color--ui-white;

    &:hover {
      border: 1px solid $color--primary;
    }
  }

  &__checked {
    border: 1px solid $color--primary;
    background: $color--primary;

    :deep .lego-icon {
      position: absolute;
      top: -1px;
      left: -1px;
      color: #fff !important;
    }
  }

  &__hide {
    display: none;
  }

  &--disabled {
    .lego-checkbox__checked {
      border-color: $color--primary-tint03;
      background: $color--primary-tint03;
    }

    .lego-checkbox__unchecked {
      border: 1px solid #dddfe4;
      background: #e3e4e8;
    }
  }

  &--readonly {
    pointer-events: none;

    .lego-checkbox__checked {
      border-color: $color--primary-tint03;
      background: $color--primary-tint03;
    }

    .lego-checkbox__unchecked {
      background: $color--ui11;
    }
  }
}
</style>
